import { useState } from "react";
import { HttpError, request } from "../../../api/http";

const ALLOWED_EXTENSIONS = [
  "pdf",
  "zip",
  "xlsx",
  "xls",
  "docx",
  "doc",
  "csv",
  "png",
  "jpg",
  "jpeg",
] as const;

const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",");

type Props = {
  brokerInternalId: number;
  token: string | null;
  onClose: () => void;
  onUploaded: () => void;
};

export default function UploadInvoiceModal({
  brokerInternalId,
  token,
  onClose,
  onUploaded,
}: Props) {
  const now = new Date();
  const [year, setYear] = useState<string>(String(now.getFullYear()));
  const [month, setMonth] = useState<string>(String(now.getMonth() + 1));
  const [amount, setAmount] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const submit = async () => {
    setError(null);
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const a = parseFloat(amount);
    if (!Number.isFinite(y) || y < 2000 || y > 2999) {
      setError("Año inválido");
      return;
    }
    if (!Number.isFinite(m) || m < 1 || m > 12) {
      setError("Mes inválido");
      return;
    }
    if (!Number.isFinite(a) || a <= 0) {
      setError("Importe inválido");
      return;
    }
    if (!file) {
      setError("Selecciona un archivo");
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
      setError(`Extensión no permitida (${ext || "ninguna"})`);
      return;
    }

    setSubmitting(true);
    try {
      const base64 = await fileToBase64(file);
      await request(
        `/api/admin/operations/brokers/${brokerInternalId}/invoices`,
        {
          method: "POST",
          token,
          body: {
            year: y,
            month: m,
            amount: a,
            extension: ext,
            base64Content: base64,
          },
        },
      );
      onUploaded();
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as { error?: string; message?: string } | undefined;
        setError(body?.message ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setError("No se pudo subir el archivo");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={dialog}>
        <header style={dialogHeader}>
          <h2 style={{ margin: 0, fontSize: ".95rem", color: "#1f2933" }}>
            Subir factura
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            ✕
          </button>
        </header>
        <div style={dialogBody}>
          <Row label="Período">
            <input
              type="number"
              value={year}
              min={2000}
              max={2999}
              onChange={(e) => setYear(e.target.value)}
              placeholder="Año"
              style={inputStyle}
            />
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={inputStyle}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={String(m)}>
                  {String(m).padStart(2, "0")}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Importe (€)">
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{ ...inputStyle, flex: 1 }}
            />
          </Row>
          <Row label="Archivo">
            <input
              type="file"
              onChange={handleFile}
              accept={ACCEPT_ATTR}
              style={{ flex: 1, fontSize: ".82rem" }}
            />
          </Row>
          {file && (
            <div style={{ fontSize: ".75rem", color: "#73879C" }}>
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
          {error && (
            <div
              style={{
                background: "#fde2e2",
                color: "#b32424",
                padding: "0.4rem 0.6rem",
                borderRadius: "0.4rem",
                fontSize: ".78rem",
              }}
            >
              {error}
            </div>
          )}
        </div>
        <footer style={dialogFooter}>
          <button
            type="button"
            onClick={onClose}
            style={ghostBtnStyle}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            style={primaryBtnStyle}
            disabled={submitting}
          >
            {submitting ? "Subiendo…" : "Subir"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "0.35fr 0.65fr",
        alignItems: "center",
        gap: "0.6rem",
        fontSize: ".82rem",
      }}
    >
      <span style={{ color: "#73879C", fontWeight: 600 }}>{label}</span>
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("FileReader returned non-string"));
        return;
      }
      // result is `data:<mimetype>;base64,<content>` — strip the data URL prefix
      // so we send only the base64 payload (mirrors the legacy uploader's
      // wire format).
      const commaIndex = result.indexOf(",");
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
};

const dialog: React.CSSProperties = {
  background: "white",
  borderRadius: "0.6rem",
  width: "min(460px, 92vw)",
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  maxHeight: "92vh",
};

const dialogHeader: React.CSSProperties = {
  padding: "0.7rem 1rem",
  borderBottom: "1px solid #e9ecef",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const closeBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#73879C",
  fontSize: "1rem",
  cursor: "pointer",
  padding: "0.2rem 0.4rem",
};

const dialogBody: React.CSSProperties = {
  padding: "0.9rem 1rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.7rem",
};

const dialogFooter: React.CSSProperties = {
  padding: "0.7rem 1rem",
  borderTop: "1px solid #e9ecef",
  display: "flex",
  gap: "0.5rem",
  justifyContent: "flex-end",
};

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".82rem",
  color: "#1f2933",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
